import React, { useState } from "react";
import { Input } from "../ui/input";
import { Plus, Trash2 } from "lucide-react";
import { selectOnFocus, parseNum } from "../../lib/inputHelpers";
import { wallsGrossFromSegments } from "../../lib/roomDefaults";

// v6 E6 — compact inline number input for horizontal deduction rows on mobile.
// Auto-selects on focus + accepts comma OR dot decimal separator (BE/NL).
function InlineNum({ value, onChange, step = "0.01", placeholder, testId }) {
  const [draft, setDraft] = useState(null);
  const display = draft !== null
    ? draft
    : (value === null || value === undefined ? "" : value);
  return (
    <Input
      type="text"
      inputMode="decimal"
      pattern="[0-9.,]*"
      autoComplete="off"
      autoCorrect="off"
      spellCheck={false}
      step={step}
      placeholder={placeholder}
      value={display}
      onFocus={(ev) => { setDraft(value === null || value === undefined ? "" : String(value)); selectOnFocus(ev); }}
      onChange={(ev) => { setDraft(ev.target.value); onChange(parseNum(ev.target.value)); }}
      onBlur={() => setDraft(null)}
      className="h-9 px-2 rounded-md font-mono text-sm text-center w-[68px] flex-shrink-0"
      data-testid={testId}
    />
  );
}

// v6 E2 — Wall deduction types (per-room).
// Each deduction = L × H × multiplier. Applies ONLY to "walls" auto_kind
// measurements (painting walls, wallpaper, primer, sanding, removing behang).
// Never reduces ceiling / floor / plinten / piece / fixed.
export const DEDUCTION_KINDS = [
  { key: "door",                label: "Deur",            defaults: { length: 0.9, height: 2.1 } },
  { key: "window",              label: "Raam",            defaults: { length: 1.5, height: 1.2 } },
  { key: "builtin_cabinet",     label: "Inbouwkast",      defaults: { length: 1.0, height: 2.4 } },
  { key: "kitchen_cabinet",     label: "Keukenkasten",    defaults: { length: 2.5, height: 2.2 } },
  { key: "wardrobe",            label: "Vaste kledingkast", defaults: { length: 2.0, height: 2.4 } },
  { key: "fixed_construction",  label: "Vaste constructie", defaults: { length: 1.0, height: 1.0 } },
  { key: "tiled_wall",          label: "Betegelde wand",  defaults: { length: 2.0, height: 1.5 } },
  { key: "other",               label: "Andere",          defaults: { length: 1.0, height: 1.0 } },
];

const KIND_BY_KEY = Object.fromEntries(DEDUCTION_KINDS.map((d) => [d.key, d]));

export default function RoomDeductionsBlock({ room, onUpdateRoom }) {
  const deductions = room.deductions || [];

  const addDeduction = (kind = "door") => {
    const k = KIND_BY_KEY[kind] || KIND_BY_KEY.other;
    const next = [
      ...deductions,
      {
        id: crypto.randomUUID(), kind, name: k.label,
        length: k.defaults.length, height: k.defaults.height, multiplier: 1,
      },
    ];
    onUpdateRoom({ deductions: next });
  };
  const updDeduction = (idx, patch) => {
    onUpdateRoom({ deductions: deductions.map((d, i) => (i === idx ? { ...d, ...patch } : d)) });
  };
  const remDeduction = (idx) => {
    onUpdateRoom({ deductions: deductions.filter((_, i) => i !== idx) });
  };

  const L = +room.length || 0;
  const W = +room.width || 0;
  const H = +room.height || 0;
  // v6 F7 — bij vrije opmeting komt bruto uit wall_segments, niet L×W×H.
  const gross = room?.measure_method === "free"
    ? wallsGrossFromSegments(room)
    : Math.max(0, 2 * (L + W) * H);
  const total = deductions.reduce((s, d) => {
    const a = (+d.length || 0) * (+d.height || 0) * Math.max(1, +d.multiplier || 1);
    return a > 0 ? s + a : s;
  }, 0);
  const net = Math.max(0, gross - total);

  return (
    <div className="border border-[#E5E2DC] rounded-xl p-3 bg-[#FAF9F6]" data-testid={`room-deductions-${room.id}`}>
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <div className="text-[11px] uppercase tracking-[0.15em] font-bold text-[#5D7259]">
          Openingen & aftrekposten
          <span className="ml-2 normal-case font-normal text-[10px] text-[#4F4F4D]">
            (alleen voor wandwerken — niet plafonds/plinten)
          </span>
        </div>
        <div className="flex items-center gap-1 flex-wrap">
          {DEDUCTION_KINDS.slice(0, 3).map((k) => (
            <button
              key={k.key}
              onClick={() => addDeduction(k.key)}
              className="text-[11px] px-2 h-7 rounded-md border border-[#E5E2DC] hover:border-[#D96C4A] hover:bg-white text-[#2C2C2A] font-medium gap-1 inline-flex items-center"
              data-testid={`add-deduction-${k.key}-${room.id}`}
            >
              <Plus className="w-3 h-3" /> {k.label}
            </button>
          ))}
          <select
            onChange={(e) => { if (e.target.value) { addDeduction(e.target.value); e.target.value = ""; } }}
            className="text-[11px] h-7 rounded-md border border-[#E5E2DC] bg-white px-1.5 text-[#2C2C2A]"
            data-testid={`add-deduction-select-${room.id}`}
            defaultValue=""
          >
            <option value="" disabled>+ Andere…</option>
            {DEDUCTION_KINDS.slice(3).map((k) => (
              <option key={k.key} value={k.key}>{k.label}</option>
            ))}
          </select>
        </div>
      </div>

      {deductions.length === 0 ? (
        <div className="text-[11px] text-[#5D7259] italic px-1">Geen openingen of aftrekposten toegevoegd</div>
      ) : (
        <div className="space-y-1.5">
          {deductions.map((d, idx) => {
            const k = KIND_BY_KEY[d.kind] || KIND_BY_KEY.other;
            const mult = Math.max(1, +d.multiplier || 1);
            const a = (+d.length || 0) * (+d.height || 0) * mult;
            return (
              <div key={d.id || idx}
                className="bg-white rounded-lg p-2 border border-[#E5E2DC]"
                data-testid={`room-deduction-row-${room.id}-${idx}`}
              >
                {/* Top row: type chip + description (always expands) + remove */}
                <div className="flex items-center gap-2 mb-1.5">
                  <span className="text-[10px] uppercase tracking-wider px-1.5 h-6 rounded bg-[#FBE9DF] text-[#A04A2D] font-bold flex items-center flex-shrink-0">
                    {k.label}
                  </span>
                  <Input
                    value={d.name || ""}
                    onChange={(e) => updDeduction(idx, { name: e.target.value })}
                    placeholder="Beschrijving"
                    className="h-8 rounded-md text-sm flex-1 min-w-0"
                    data-testid={`room-deduction-name-${room.id}-${idx}`}
                  />
                  <button
                    onClick={() => remDeduction(idx)}
                    className="w-7 h-7 rounded-md hover:bg-red-50 flex items-center justify-center text-[#D9534F] flex-shrink-0"
                    data-testid={`room-deduction-remove-${room.id}-${idx}`}
                    aria-label="Verwijder aftrek"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
                {/* Inline horizontal calc row — L × H × qty = X m² (compact for mobile) */}
                <div className="flex items-center gap-1.5 flex-wrap">
                  <InlineNum value={d.length}
                    onChange={(v) => updDeduction(idx, { length: v })}
                    placeholder="L"
                    testId={`room-deduction-L-${room.id}-${idx}`} />
                  <span className="text-[#A8A7A0] text-xs">×</span>
                  <InlineNum value={d.height}
                    onChange={(v) => updDeduction(idx, { height: v })}
                    placeholder="H"
                    testId={`room-deduction-H-${room.id}-${idx}`} />
                  <span className="text-[#A8A7A0] text-xs">×</span>
                  <InlineNum value={mult} step="1" integer
                    onChange={(v) => updDeduction(idx, { multiplier: Math.max(1, Math.round(+v || 1)) })}
                    placeholder="qty"
                    testId={`room-deduction-mult-${room.id}-${idx}`} />
                  <span className="text-[#A8A7A0] text-xs ml-auto">=</span>
                  <span className="text-sm font-mono font-bold text-[#2C2C2A] text-right whitespace-nowrap">
                    {a.toFixed(2)} m²
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Summary */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 pt-2 mt-2 border-t border-[#E5E2DC] text-xs"
        data-testid={`room-walls-summary-${room.id}`}>
        <span>Bruto wand: <strong className="font-mono">{gross.toFixed(2)} m²</strong></span>
        <span className="text-[#D9534F]">− Aftrekposten: <strong className="font-mono">{total.toFixed(2)} m²</strong></span>
        <span className="ml-auto text-sm">
          Netto wand: <strong className="font-mono text-[#D96C4A]" data-testid={`room-walls-net-${room.id}`}>
            {net.toFixed(2)} m²
          </strong>
        </span>
      </div>
    </div>
  );
}
